/**
 * Payment Approvals — cash/agent queue with Token & Device Wizard.
 * Mobile-money pending rows are listed as awaiting provider (not approvable here).
 * Approvers: business_admin, commercial_officer (platform system_admin is ops-only).
 */
import { useCallback, useEffect, useState } from "react";
import { ColumnDef } from "@tanstack/react-table";
import { AppBadge, AppButton, AppCard } from "../../../shared/components/ui";
import { AppDataTable } from "../../../shared/components/AppDataTable";
import { PageHeader, PageState } from "../../../shared/components/PageChrome";
import { api } from "../../../shared/api/client";
import {
  TokenDeviceWizard,
  isCashLike,
  isMobileLike,
  type WizardPayment,
} from "../../devices/components/TokenDeviceWizard";

function methodBadge(method: string) {
  return <AppBadge tone={isCashLike(method) ? "warning" : "info"}>{method.replace(/_/g, " ")}</AppBadge>;
}

function formatTzs(amount: number): string {
  return `TZS ${amount.toLocaleString("en-US")}`;
}

export function PaymentApprovalQueue() {
  const [payments, setPayments] = useState<WizardPayment[]>([]);
  const [pageState, setPageState] = useState<"loading" | "error" | "success">("loading");
  const [pageError, setPageError] = useState("");
  const [wizardPayment, setWizardPayment] = useState<WizardPayment | null>(null);

  const load = useCallback(async () => {
    setPageState("loading");
    try {
      const data = await api.get<{ payments: WizardPayment[] }>(
        "/payments?status=pending&per_page=50",
      );
      setPayments(data.payments ?? []);
      setPageState("success");
    } catch (ex: unknown) {
      setPageError(ex instanceof Error ? ex.message : "Failed to load pending payments");
      setPageState("error");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const cashPending = payments.filter((p) => isCashLike(p.method));
  const mobilePending = payments.filter((p) => isMobileLike(p.method));

  const columns: ColumnDef<WizardPayment, unknown>[] = [
    { accessorKey: "payment_reference", header: "Reference" },
    { id: "customer", header: "Customer", accessorFn: (r) => r.customer_name ?? `#${r.customer_id}` },
    { id: "amount", header: "Amount", accessorFn: (r) => formatTzs(r.amount) },
    {
      id: "days",
      header: "Days",
      cell: ({ row }) => {
        const p = row.original;
        const days = p.estimated_days ?? p.service_days ?? p.token_days;
        if (days == null) return "—";
        return (
          <span title={p.estimated_days_message ?? undefined}>
            ≈ {days} day{days === 1 ? "" : "s"}
          </span>
        );
      },
    },
    { id: "method", header: "Method", cell: ({ row }) => methodBadge(row.original.method) },
    { id: "device", header: "Device", accessorFn: (r) => r.device_serial ?? "—" },
    { id: "status", header: "Status", cell: () => <AppBadge tone="warning">Pending</AppBadge> },
    {
      id: "actions", header: "", enableSorting: false,
      cell: ({ row }) => {
        const p = row.original;
        if (!isCashLike(p.method)) {
          return <AppBadge tone="neutral">Awaiting provider</AppBadge>;
        }
        return (
          <AppButton
            type="button"
            title="Open approve wizard"
            onClick={(e) => { e.stopPropagation(); setWizardPayment(p); }}
          >
            Approve…
          </AppButton>
        );
      },
    },
  ];

  const listStatus =
    pageState === "success" && cashPending.length === 0 && mobilePending.length === 0
      ? "empty"
      : pageState;

  return (
    <div>
      <TokenDeviceWizard
        open={!!wizardPayment}
        payment={wizardPayment}
        deviceSerial={wizardPayment?.device_serial}
        initialIntent="release_days"
        onClose={() => setWizardPayment(null)}
        onDone={load}
      />

      <PageHeader
        title="Payment Approvals"
        subtitle="Cash / agent collections — Business Admin or Commercial Officer approve to release PAYG days. The Days column shows how many days that amount unlocks. Mobile money completes via provider webhook."
      />

      <PageState
        status={listStatus}
        error={pageError}
        onRetry={load}
        emptyTitle="No payments waiting"
        emptyHint="Cash collections to approve will appear here. Mobile money pending waits for the provider."
      >
        {cashPending.length > 0 ? (
          <AppCard title="Cash / agent — ready to approve">
            <AppDataTable data={cashPending} columns={columns} />
          </AppCard>
        ) : null}
        {mobilePending.length > 0 ? (
          <AppCard title="Mobile money — awaiting provider webhook">
            <p className="pg-text-muted" style={{ marginTop: 0 }}>
              These stay pending until Vodacom/Airtel/Tigo confirms. Do not force-approve here.
            </p>
            <AppDataTable
              data={mobilePending}
              columns={columns.filter((c) => c.id !== "actions" || true)}
            />
          </AppCard>
        ) : null}
      </PageState>
    </div>
  );
}
